import { Controller } from '@hotwired/stimulus'
import { CheckboxTree } from '../modules/checkbox_tree'

export default class extends Controller<HTMLElement> {
  static targets = ['checkbox', 'searchInput', 'emptyState']

  static values = {
    allUnchecked: Boolean
  }

  declare checkboxTargets: HTMLInputElement[]
  declare searchInputTarget: HTMLInputElement
  declare hasSearchInputTarget: boolean
  declare emptyStateTarget: HTMLElement
  declare hasEmptyStateTarget: boolean
  declare allUncheckedValue: boolean

  private nodesTree: CheckboxTree | null = null
  private readonly boundHandleChange = this.handleCheckboxChange.bind(this)
  private readonly boundSearch = this.handleSearchInput.bind(this)
  private searchTimeout: ReturnType<typeof setTimeout> | null = null

  connect (): void {
    this.nodesTree = new CheckboxTree(this.checkboxTargets)
    this.attachListeners()
    this.initializeState()
  }

  disconnect (): void {
    this.checkboxTargets.forEach(cb => {
      cb.removeEventListener('change', this.boundHandleChange)
    })

    if (this.hasSearchInputTarget) {
      this.searchInputTarget.removeEventListener('input', this.boundSearch)
    }

    this.clearSearchTimeout()
  }

  private attachListeners (): void {
    this.checkboxTargets.forEach(cb => {
      cb.addEventListener('change', this.boundHandleChange)
    })

    if (this.hasSearchInputTarget) {
      this.searchInputTarget.addEventListener('input', this.boundSearch)
    }
  }

  private initializeState (): void {
    if (this.nodesTree == null) return

    queueMicrotask(() => this.dispatchChangeEvent())

    if (this.allUncheckedValue) {
      this.nodesTree.allUnchecked = true
    }

    this.nodesTree.updateTreeFromLeaves()
  }

  private handleSearchInput (event: Event): void {
    if (this.nodesTree == null) return

    this.clearSearchTimeout()

    this.searchTimeout = setTimeout(() => {
      const searchValue = (event.target as HTMLInputElement).value
      this.nodesTree?.filterTree(searchValue)
      this.updateEmptyState()
    }, 200)
  }

  private clearSearchTimeout (): void {
    if (this.searchTimeout !== null) {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = null
    }
  }

  private handleCheckboxChange (event: Event): void {
    if (this.nodesTree == null) return

    const target = event.target as HTMLInputElement
    const node = this.nodesTree.findNodeByElement(target)
    if (node == null) return

    this.nodesTree.updateNodeState(node, target.checked)
    this.nodesTree.propagateChanges(node)
    this.dispatchChangeEvent()
  }

  private updateEmptyState (): void {
    if (!this.hasEmptyStateTarget || this.nodesTree == null) return
    const state = this.nodesTree.visibleNodesCount === 0

    console.log(this.nodesTree.visibleNodesCount)

    if (state) {
      this.emptyStateTarget.classList.remove('hidden')
    } else {
      this.emptyStateTarget.classList.add('hidden')
    }
  }

  private dispatchChangeEvent (): void {
    if (this.nodesTree == null) return

    const rootNode = this.nodesTree.getRootNode()
    if (rootNode == null) return

    const { checkedTree, uncheckedTree } = this.nodesTree.buildJsonTrees()

    const event = new CustomEvent('checkbox-tree:changed', {
      bubbles: true,
      detail: { checkedTree, uncheckedTree }
    })

    rootNode.el.dispatchEvent(event)
  }
}
